import { useCallback, useEffect, useReducer, useRef } from "react"
import { USE_MOCK_BATTLE } from "@/constants/guardians"
import { useAuth } from "@/context/AuthContext"
import {
  BattleResultPayload,
  BattleStartedPayload,
} from "@/types/battle"
import {
  connectBattleSocket,
  consumePendingBattleStarted,
  emitBattleEvent,
  onBattleEvent,
} from "@/socket/battleSocket"
import { mapBattleResult } from "@/features/duel/mapBattleResult"
import { duelReducer, initialDuelState } from "@/features/duel/duelReducer"
import {
  enrichBattleCardsShardsFromSource,
  normalizeBattleCardView,
} from "@/utils/battleCards"
import { normalizeBattlePlayerInfo } from "@/utils/challengePayload"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

function normalizeBattleStarted(
  raw: BattleStartedPayload,
  collectionCards?: {
    id: string
    shardSlots?: BattleStartedPayload["yourCards"][number]["shardSlots"]
  }[]
): BattleStartedPayload & { selectionDeadlineAt: number } {
  const extended = raw as BattleStartedPayload & {
    remainingSelectionSec?: number
    card_selection_timeout_sec?: number
    selectionDeadlineAt?: string | number
    cardSelectionDeadlineAt?: string | number
  }
  const timeout =
    extended.cardSelectionTimeoutSec ??
    extended.remainingSelectionSec ??
    extended.card_selection_timeout_sec ??
    60

  const serverDeadline =
    extended.selectionDeadlineAt ?? extended.cardSelectionDeadlineAt
  let deadline = Date.now() + Math.max(0, Math.floor(timeout)) * 1000
  if (serverDeadline !== undefined) {
    const ts =
      typeof serverDeadline === "string"
        ? Date.parse(serverDeadline)
        : serverDeadline
    if (!Number.isNaN(ts)) deadline = ts
  }

  const yourCards = enrichBattleCardsShardsFromSource(
    (raw.yourCards ?? [])
      .map((card) => normalizeBattleCardView(card))
      .filter((card): card is NonNullable<typeof card> => card != null),
    collectionCards
  )

  return {
    ...raw,
    you: normalizeBattlePlayerInfo(raw.you) ?? raw.you,
    opponent: normalizeBattlePlayerInfo(raw.opponent) ?? raw.opponent,
    yourCards,
    cardSelectionTimeoutSec: Math.max(
      0,
      Math.ceil((deadline - Date.now()) / 1000)
    ),
    selectionDeadlineAt: deadline,
  }
}

export function useDuelMachine(initialBattle?: BattleStartedPayload) {
  const { token } = useAuth()
  const { collectionCards } = useGuardiansPassport()
  const collectionRef = useRef(collectionCards)
  collectionRef.current = collectionCards
  const [state, dispatch] = useReducer(duelReducer, initialDuelState)
  const battleRef = useRef(state.battle)
  battleRef.current = state.battle
  const activeBattleIdRef = useRef<string | null>(null)

  const isRelevantBattle = useCallback((battleId: string) => {
    const current = activeBattleIdRef.current ?? battleRef.current?.battleId
    return !!current && current === battleId
  }, [])

  const startBattle = useCallback((payload: BattleStartedPayload) => {
    const normalized = normalizeBattleStarted(payload, collectionRef.current)
    if (
      activeBattleIdRef.current === normalized.battleId &&
      battleRef.current?.battleId === normalized.battleId
    ) {
      return
    }
    activeBattleIdRef.current = normalized.battleId
    dispatch({ type: "BATTLE_STARTED", payload: normalized })
  }, [])

  const applyResult = useCallback((result: BattleResultPayload) => {
    const battle = battleRef.current
    if (!battle) return
    dispatch({ type: "START_REVEAL", payload: result })
    return mapBattleResult(battle, result)
  }, [])

  useEffect(() => {
    if (USE_MOCK_BATTLE) return
    if (initialBattle) startBattle(initialBattle)
  }, [initialBattle, startBattle])

  useEffect(() => {
    if (USE_MOCK_BATTLE) return

    let cleanups: (() => void)[] = []
    let cancelled = false

    const setup = async () => {
      try {
        await connectBattleSocket(token)
      } catch {
        return
      }
      if (cancelled) return

      cleanups = [
        onBattleEvent("battle-started", (payload) => startBattle(payload)),
        onBattleEvent("card-placed", (payload) => {
          if (!isRelevantBattle(payload.battleId)) return
          dispatch({ type: "CARD_PLACED", payload })
        }),
        onBattleEvent("opponent-selected", (payload) => {
          if (!isRelevantBattle(payload.battleId)) return
          dispatch({
            type: "OPPONENT_SELECTED",
            payload: { opponentReady: payload.opponentReady },
          })
        }),
        onBattleEvent("battle-result", (payload) => {
          if (!isRelevantBattle(payload.battleId)) return
          applyResult(payload)
        }),
        onBattleEvent("battle-cancelled", (payload) => {
          if (!isRelevantBattle(payload.battleId)) return
          dispatch({ type: "BATTLE_CANCELLED", payload })
        }),
      ]

      const pending = consumePendingBattleStarted()
      if (pending && !battleRef.current) startBattle(pending)
    }

    void setup()
    return () => {
      cancelled = true
      cleanups.forEach((fn) => fn())
    }
  }, [token, applyResult, startBattle, isRelevantBattle])

  useEffect(() => {
    const timerActive =
      state.phase === "selecting" ||
      state.phase === "card_placed" ||
      state.phase === "opponent_ready"
    if (!timerActive) return
    const interval = setInterval(() => dispatch({ type: "TICK_TIMER" }), 1000)
    return () => clearInterval(interval)
  }, [state.phase])

  const selectCard = useCallback(
    (cardId: string, abilityId?: string | null) => {
      if (state.phase !== "selecting" || state.yourCard) return
      if (!state.battle) return
      const card = state.battle.yourCards.find((c) => c.id === cardId)
      if (!card) return

      dispatch({
        type: "CARD_PLACED",
        payload: {
          battleId: state.battle.battleId,
          slot: state.yourSlot ?? "A",
          card,
          opponentReady: state.opponentReady,
        },
      })

      emitBattleEvent("select-card", {
        battleId: state.battle.battleId,
        cardId,
        ...(abilityId ? { abilityId } : {}),
      })
    },
    [
      state.phase,
      state.yourCard,
      state.yourSlot,
      state.battle,
      state.opponentReady,
    ]
  )

  const reset = useCallback(() => {
    activeBattleIdRef.current = null
    dispatch({ type: "RESET" })
  }, [])

  const revealDone = useCallback(() => {
    dispatch({ type: "REVEAL_DONE" })
  }, [])

  const mappedResult =
    state.battle && state.result
      ? mapBattleResult(state.battle, state.result)
      : null

  return { state, mappedResult, selectCard, reset, revealDone }
}
